// Money labels for the Finances charts (SHER-853).
//
// The axis used to print `formatter: value => value.toString()`, so a CHOM month of
// 52 703 227 FCFA rendered as `52703227` -- unreadable at axis size, and with nothing
// saying it was money at all next to a card header that showed a document COUNT.
//
// Two formatters on purpose. An axis label has about sixty pixels and has to abbreviate;
// a tooltip is read deliberately and has to be exact. One shared formatter would have to
// give up one of the two.

import { getCurrentLangIndex } from '../../../../utils/translate';

const LOCALES = ['en', 'es', 'fr'];

// Grouping and decimal characters are language-dependent -- 52.7M in English is 52,7M in
// French -- and the rest of the app already formats money against the active language
// (see formatCurrency in utils/index.jsx). Resolved per call rather than captured once,
// because the language can change without remounting the chart.
const currentLocale = (): string => LOCALES[getCurrentLangIndex()] || 'en';

const UNITS: { threshold: number; suffix: string }[] = [
  { threshold: 1e9, suffix: 'B' },
  { threshold: 1e6, suffix: 'M' },
  { threshold: 1e3, suffix: 'K' },
];

// CHOM and Cardio24 both bill in CFA francs. Callers pass the tenant's configured
// currency (investigations.currentInvestigation.billingInfo.currency); this is only the
// fallback for the charts that render before it resolves.
export const DEFAULT_CURRENCY = 'FCFA';

const groupDigits = (value: number, locale: string, digits: number): string =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);

/**
 * Short money label for an axis tick or a bar's data label: `60M CFA`, `52.7M CFA`.
 * A decimal appears only when it changes the reading, so a round 60 million does not
 * become `60.0M`.
 */
export const formatCompactMoney = (
  value: number,
  currency: string = DEFAULT_CURRENCY,
  locale: string = currentLocale(),
): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    // The analytics endpoints do answer with nulls (SHER-846). An axis reading
    // "NaN CFA" is worse than an axis reading nothing.
    return '';
  }

  const magnitude = Math.abs(value);
  const unit = UNITS.find((candidate) => magnitude >= candidate.threshold);
  if (!unit) {
    return `${groupDigits(value, locale, 0)} ${currency}`;
  }

  const scaled = value / unit.threshold;
  const rounded = Math.round(scaled * 10) / 10;
  const digits = Number.isInteger(rounded) ? 0 : 1;
  return `${groupDigits(rounded, locale, digits)}${unit.suffix} ${currency}`;
};

/**
 * Exact money label for a tooltip: every digit, grouped, with the currency. CFA is a
 * zero-decimal currency, so no cents are printed.
 */
export const formatFullMoney = (
  value: number,
  currency: string = DEFAULT_CURRENCY,
  locale: string = currentLocale(),
): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '';
  }
  return `${groupDigits(value, locale, 0)} ${currency}`;
};
